import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@altimateai/lego";
import { Button } from "@altimateai/lego";
import { Checkbox } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { useAppState } from "@/modules/app/useAppContext";
import { Company } from "@/helpers/types";
import { isSSOEnabled } from "@/helpers/tenant";

interface CompanySwitcherProps {
  isOpen: boolean;
  onClose: () => void;
}

const CompanySwitcher: React.FC<CompanySwitcherProps> = ({ isOpen, onClose }) => {
  const { companyInfo } = useAppState();
  const companies = companyInfo?.other_tenants_in_group || [];
  const [openInSameTab, setOpenInSameTab] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("companySwitcher_openInSameTab");
    setOpenInSameTab(stored === "true");
  }, []);

  const handleCheckboxChange = (checked: boolean) => {
    setOpenInSameTab(checked);
    localStorage.setItem("companySwitcher_openInSameTab", checked.toString());
  };

  const handleCompanySwitch = (company: Company) => {
    const currentUrl = window.location.origin;
    const baseUrl = currentUrl.replace(
      /^https?:\/\/[^.]*/,
      `https://${encodeURIComponent(company.name)}`
    );
    const redirectPath = isSSOEnabled() ? "/login-redirect/okta" : "/";
    const url = `${baseUrl}${redirectPath}`;

    if (openInSameTab) {
      window.location.href = url;
    } else {
      window.open(url, "_blank");
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="al-max-w-md">
        <DialogHeader>
          <DialogTitle>Switch Tenants</DialogTitle>
          <DialogDescription>Select a tenant to switch to.</DialogDescription>
        </DialogHeader>
        <Stack gap="sm" direction="column">
          {companies.map((company: Company, index: number) => (
            <Button
              key={index}
              variant="outline"
              className="al-w-full al-justify-between"
              onClick={() => handleCompanySwitch(company)}
            >
              <span>{company.name}</span>
              <span>→</span>
            </Button>
          ))}
          {companies.length === 0 && (
            <p className="al-text-sm al-text-muted-foreground al-text-center al-py-4">
              No additional tenants available.
            </p>
          )}
          <Stack direction="row" align="center" gap="xs" className="al-pt-4 al-border-t">
            <Checkbox
              id="openInSameTab"
              checked={openInSameTab}
              onCheckedChange={handleCheckboxChange}
            />
            <label
              htmlFor="openInSameTab"
              className="al-text-sm al-font-medium al-leading-none al-peer-disabled:cursor-not-allowed al-peer-disabled:opacity-70"
            >
              Open in same tab
            </label>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default CompanySwitcher;